import { httpClient } from "common/HttpClient/http-client";
import { getRequest, patchRequest, postRequest } from "common/HttpClient/http-utility";
import {
  isFiniteNumber,
  positiveFiniteNumberOr,
  trimmedParam,
} from "common/utils/valueChecks";
import { resolveContextNodeIdForTagRow } from "./nodeListRowsMapper";

const CONTEXT_NODES_BASE = "/platform-admin-service/v1/context-nodes";
const TAGS_BASE = "/platform-admin-service/v1/tags";
const BULK_CONTEXTUALIZE_UNIFIED = `${TAGS_BASE}/bulk-contextualize`;
const NODE_TYPES_REFERENCE = "/platform-admin-service/v1/reference-data/node-types";

const DEFAULT_PAGE_SIZE = 250;
const PARENT_NODE_TREE_PICKER_FETCH_SIZE = 2000;
const BULK_CONTEXTUALIZE_MAX_TAGS = 250;

export function extractPagedNodesList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  return [];
}

function extractTotalPages(payload) {
  const pageMeta =
    payload?.metadata ??
    payload?.pagination ??
    payload?.page ??
    payload?.data?.page ??
    payload?.data?.metadata ??
    {};
  const totalPages = pageMeta.totalPages;
  if (typeof totalPages === "number" && totalPages > 0) return totalPages;
  return 1;
}

export function extractTotalRowCountFromPagedPayload(payload) {
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }
  const pageMeta =
    payload.metadata ??
    payload.pagination ??
    payload.page ??
    payload.data?.page ??
    payload.data?.metadata ??
    {};
  const total =
    pageMeta.totalItems ??
    pageMeta.totalCount ??
    pageMeta.totalElements ??
    (typeof pageMeta.total === "number" ? pageMeta.total : null);
  if (typeof total === "number" && total >= 0) return total;
  return null;
}

const TAGS_LIST_STRING_PARAMS = [
  "nodeName",
  "nodeType",
  "nodeSubType",
  "sourceTag",
  "contextTag",
  "sourceConnector",
  "source",
  "parentNodeName",
  "search",
];

export function buildTagsListQuery(params = {}) {
  const query = {
    page: params.page ?? 0,
    size: params.size ?? DEFAULT_PAGE_SIZE,
    sort: params.sort ?? "displayName,asc",
  };

  for (const key of TAGS_LIST_STRING_PARAMS) {
    const value = trimmedParam(params, key);
    if (value !== "") query[key] = value;
  }

  if (typeof params.isContextualized === "boolean") {
    query.isContextualized = params.isContextualized;
  }

  return query;
}

export function buildContextNodesListQuery(params = {}) {
  return {
    page: params.page ?? 0,
    size: params.size ?? DEFAULT_PAGE_SIZE,
    sort: params.sort ?? "id,asc",
    ...(params.search != null && params.search !== ""
      ? { search: params.search }
      : {}),
    ...(params.edgeTag != null && params.edgeTag !== ""
      ? { edgeTag: params.edgeTag }
      : {}),
    ...(params.nodeType != null && params.nodeType !== ""
      ? { nodeType: params.nodeType }
      : {}),
    ...(params.nodeName != null && params.nodeName !== ""
      ? { nodeName: params.nodeName }
      : {}),
    ...(params.sparkplugMetricTagContains != null &&
      params.sparkplugMetricTagContains !== ""
      ? { sparkplugMetricTagContains: params.sparkplugMetricTagContains }
      : {}),
  };
}

export function buildTagsExportQuery(
  sourceConnector,
  sort = "displayName,asc"
) {
  const query = {
    export: true,
    download: true,
    sort,
  };

  const connector =
    typeof sourceConnector === "string" ? sourceConnector.trim() : "";
  if (connector !== "") {
    query.sourceConnector = connector;
  }

  return query;
}

export async function fetchTagsPage(params) {
  return getRequest(TAGS_BASE, buildTagsListQuery(params));
}

export async function fetchContextNodesPage(params) {
  return getRequest(CONTEXT_NODES_BASE, buildContextNodesListQuery(params));
}

export async function fetchAllTagContextNodes(queryOverrides = {}) {
  const aggregated = [];
  let pageNumber = 0;
  let totalPages = 1;

  while (pageNumber < totalPages) {
    const response = await fetchContextNodesPage({
      page: pageNumber,
      size: DEFAULT_PAGE_SIZE,
      sort: "id,asc",
      ...queryOverrides,
    });

    if (response.error || response.status >= 400) {
      return {
        status: response.status ?? 500,
        error: response.error ?? "Failed to load tags",
        data: null,
      };
    }

    const payload = response.data ?? {};
    const content = extractPagedNodesList(payload);
    totalPages = extractTotalPages(payload);

    aggregated.push(...content);
    pageNumber += 1;

    if (content.length === 0) {
      break;
    }
  }

  return { status: 200, data: aggregated };
}

export async function fetchParentCandidates(allowedParentTypeNames, options = {}) {
  const pageSize = positiveFiniteNumberOr(
    options.pageSize,
    PARENT_NODE_TREE_PICKER_FETCH_SIZE
  );

  const allowedSet = new Set(
    allowedParentTypeNames
      .map((t) => String(t ?? "").trim().toLowerCase())
      .filter((t) => t !== "")
  );
  if (allowedSet.size === 0) {
    return { status: 200, data: [] };
  }

  const byId = new Map();
  let pageNumber = 0;
  let totalPages = 1;

  while (pageNumber < totalPages) {
    const response = await fetchContextNodesPage({
      page: pageNumber,
      size: pageSize,
      sort: "id,asc",
    });

    if (response.error || response.status >= 400) {
      return {
        status: response.status ?? 500,
        error: response.error ?? "Failed to load parent candidates",
        data: null,
      };
    }

    const payload = response.data ?? {};
    const content = extractPagedNodesList(payload);
    totalPages = extractTotalPages(payload);

    for (const dto of content) {
      const nt = String(dto?.nodeType ?? "").toLowerCase();
      if (!allowedSet.has(nt)) continue;
      const id = dto?.nodeId;
      if (id == null) continue;
      const key = String(id);
      if (!byId.has(key)) {
        const parentIdRaw = dto?.parentNodeId;
        const parentNodeId = isFiniteNumber(parentIdRaw)
          ? Number(parentIdRaw)
          : null;
        byId.set(key, {
          nodeId: id,
          nodeName:
            dto.nodeName == null || dto.nodeName === ""
              ? ""
              : String(dto.nodeName),
          nodeType:
            dto.nodeType == null || dto.nodeType === ""
              ? ""
              : String(dto.nodeType),
          parentNodeId,
        });
      }
    }

    pageNumber += 1;
    if (content.length === 0) break;
  }

  return { status: 200, data: [...byId.values()] };
}

export async function fetchNodeTypesReference() {
  return getRequest(NODE_TYPES_REFERENCE);
}

export async function fetchContextNodeById(nodeId) {
  if (nodeId == null || String(nodeId).trim() === "") {
    return { error: "Missing node id" };
  }
  return getRequest(`${CONTEXT_NODES_BASE}/${nodeId}`);
}

async function lookupContextNodeDtoForTagRow(row) {
  if (row == null || row.uncontextualized) {
    return null;
  }

  const contextNodeId = resolveContextNodeIdForTagRow(row);
  if (contextNodeId == null) {
    return null;
  }

  const result = await fetchContextNodeById(contextNodeId);
  if (result?.error || !result?.data) {
    return null;
  }
  return result.data;
}

export async function resolveContextNodeIdForTagRowAsync(row) {
  return resolveContextNodeIdForTagRow(row);
}

export async function fetchContextNodeForTagRow(row) {
  if (row == null || row.uncontextualized) {
    return { error: "Row is not contextualized" };
  }
  const dto = await lookupContextNodeDtoForTagRow(row);
  if (!dto) {
    return { error: "Could not resolve context node id" };
  }
  return { data: dto };
}

export async function patchContextNodes(body) {
  return patchRequest(CONTEXT_NODES_BASE, body);
}

export async function bulkContextualizeUnifiedTags(body) {
  return postRequest(BULK_CONTEXTUALIZE_UNIFIED, body);
}

export { BULK_CONTEXTUALIZE_MAX_TAGS };

export async function exportTagsCsv({ sourceConnector, sort } = {}) {
  try {
    const params = buildTagsExportQuery(sourceConnector, sort);
    const response = await httpClient.get(TAGS_BASE, {
      params,
      responseType: "blob",
      timeout: 0,
      headers: { Accept: "text/csv" },
    });
    return { status: response.status, data: response.data };
  } catch (error) {
    return {
      status: error?.response?.status ?? 500,
      error: error?.response?.data?.message ?? error?.message ?? "Export failed",
    };
  }
}

const TAGS_EXPORT_JOBS_BASE = "/platform-admin-service/v1/jobs";

export const TAGS_EXPORT_JOB_POLL_INTERVAL_MS = 2000;

export const TAGS_EXPORT_JOB_STATES = {
  NEW: "NEW",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
};

const TAGS_EXPORT_JOB_ACTIVE_STATES = new Set([
  TAGS_EXPORT_JOB_STATES.NEW,
  TAGS_EXPORT_JOB_STATES.IN_PROGRESS,
]);

function buildTagsExportJobPath(source) {
  const params = new URLSearchParams({
    type: "EXPORT",
    source,
  });
  return `${TAGS_EXPORT_JOBS_BASE}?${params.toString()}`;
}

function extractExportJobBody(data) {
  if (data == null || typeof data !== "object" || Array.isArray(data)) {
    return null;
  }
  const job = data.response ?? data;
  if (job == null || typeof job !== "object" || Array.isArray(job)) {
    return null;
  }
  return job;
}

export function extractJobIdFromResponse(data) {
  const job = extractExportJobBody(data);
  const id = job?.jobId;
  if (id == null || String(id).trim() === "") {
    return null;
  }
  return id;
}

export function extractExportJobState(job) {
  if (!job) {
    return "";
  }
  return String(job?.state ?? "").toUpperCase();
}

export function readJobApiErrorMessage(err, fallback = "Request failed.") {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

export function readJobFailureMessage(job) {
  return job?.errorMessage ?? job?.result?.errorMessage ?? null;
}

export function readBulkContextualizeResultTagKey(resultItem) {
  if (resultItem == null || typeof resultItem !== "object") {
    return "node";
  }
  const key = resultItem.sourceTag;
  return key != null && String(key).trim() !== "" ? String(key) : "node";
}

const stripContentTypeForFormData = (data, headers) => {
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    if (typeof headers?.delete === "function") {
      headers.delete("Content-Type");
    } else if (headers) {
      delete headers["Content-Type"];
    }
  }
  return data;
};

export function postExportJob(source) {
  const formData = new FormData();

  return httpClient.post(buildTagsExportJobPath(source), formData, {
    transformRequest: [stripContentTypeForFormData],
    validateStatus: (status) =>
      status === 202 || (status >= 200 && status < 300),
  });
}

export function getJobExportDownloadUrl(job) {
  return job?.result?.presignedUrl;
}

export function getJobExportFileName(job) {
  return job?.result?.fileName;
}

function getTagsExportJob(jobId) {
  return httpClient.get(
    `${TAGS_EXPORT_JOBS_BASE}/${encodeURIComponent(jobId)}`
  );
}

export async function pollPlatformJobUntilTerminal(
  jobId,
  {
    intervalMs = TAGS_EXPORT_JOB_POLL_INTERVAL_MS,
    shouldCancel = () => false,
    onTick,
  } = {}
) {
  while (!shouldCancel()) {
    const { data } = await getTagsExportJob(jobId);
    const job = extractExportJobBody(data);
    onTick?.(job);
    const state = extractExportJobState(job);
    if (!TAGS_EXPORT_JOB_ACTIVE_STATES.has(state)) {
      return job;
    }
    await new Promise((resolve) => {
      window.setTimeout(resolve, intervalMs);
    });
  }
  return null;
}
